/**
 * src/lib/entitlements-store.js
 *
 * Entitlement-aware navigation store: caches ONE one-shot read of
 * companies/{tid}/entitlements/current for the whole app session and exposes
 * a synchronous isModuleEnabled(key) predicate the nav layer
 * (shell/pillars.js isItemVisible) can consult without any per-item async.
 *
 * WHY a direct Firestore read (not a CF):
 *  - getSessionBootstrap (functions/src/auth/session_bootstrap.ts) does NOT
 *    carry entitlements, and getOrgAccess (which does, via
 *    entitlements.enabledKeys) is role==='admin' only — useless as a source
 *    for shaping every role's rail.
 *  - firestore.rules `match /entitlements/{doc}` allows read for ANY
 *    authenticated tenant member (`isAuth() && isInCompany(companyId)`,
 *    write:false) — a rules-permitted read, no new CF needed.
 *  - Only the browser's real-time Listen channel 503-loops against
 *    me-central2; ONE-SHOT getDoc reads succeed (same precedent as
 *    screens/messages.js's guardedRead(getDoc(...)) usage).
 *
 * FAIL-OPEN BY DESIGN: this is chrome-shaping only, never the security
 * boundary. Every module-gated read/mutate CF re-checks
 * entitlements/current server-side FAIL-CLOSED (D4 standard — throws
 * permission-denied unless modules[key]===true). If this read times out,
 * errors, or the doc/modules map is absent (older tenant never backfilled),
 * we show the full role-filtered nav rather than stranding a paying tenant
 * behind a transient read failure — exactly the same fail-open posture as
 * mountShell's role===null case (see shell.js header). Hiding happens ONLY
 * on a definitively-loaded modules map where modules[key] !== true.
 *
 * DC-05 N/A: zero writes, zero finance_postings — read-only chrome shaping.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase.js';
import { guardedRead } from './api.js';

/** The loaded modules map ({ [key]: boolean }) — null until a DEFINITIVE
 * successful load lands; stays null on timeout/error/missing doc (fail-open). */
let _modules = null;

/** In-flight/settled load promise — dedupes concurrent callers. */
let _loadPromise = null;

/**
 * loadEntitlements — kick off (or join) the one session-wide entitlements
 * read. NEVER rejects: resolves with the modules map, or null on any
 * failure/absence (fail-open — see file header). Bounded at 8s so shell
 * mount is never stranded behind it (the rail simply renders un-gated and
 * the server still fail-closes every actual call).
 *
 * @param {string|null} companyId — the tenant id from the session bootstrap
 *   payload (data.companyId). null/'' → resolves null immediately.
 * @param {Object} [opts]
 * @param {boolean} [opts.force=false] — bypass the cache and refetch.
 * @returns {Promise<Object|null>}
 */
export function loadEntitlements(companyId, opts = {}) {
  const { force = false } = opts;
  if (!companyId) return Promise.resolve(_modules);
  if (_loadPromise && !force) return _loadPromise;

  const ref = doc(db, 'companies', companyId, 'entitlements', 'current');
  _loadPromise = guardedRead(getDoc(ref), {
    // 8s, NOT the screens' 25s: this read sits on the critical path between
    // bootstrap-resolved and first rail render. A slow/failed read renders
    // the un-gated (fail-open) rail; the promise may still settle later and
    // the next navigation re-render picks the loaded map up.
    timeoutMs: 8000,
    label: 'entitlements/current',
  }).then((result) => {
    const snap = result && result.data;
    if (result.timedOut || result.error || !snap || !snap.exists()) {
      // Not definitive — leave _modules null (fail-open) and clear the
      // cached promise so a later loadEntitlements() can genuinely retry
      // (mirrors session-store.js's failed-bootstrap non-memoization fix).
      _loadPromise = null;
      return _modules;
    }
    const data = snap.data() || {};
    const modules = data.modules;
    // Only a real map is definitive; a doc without `modules` stays fail-open.
    _modules = modules && typeof modules === 'object' ? modules : null;
    return _modules;
  });
  return _loadPromise;
}

/**
 * isModuleEnabled — synchronous predicate for nav gating.
 *
 * @param {string} key — entitlement module key (e.g. 'construction',
 *   'hospitality', 'manufacturing', 'crm', 'sales', 'arap',
 *   'comp/commission', 'finance/fixed_assets', 'finance/budgeting',
 *   'finance/cash_ledger', 'service/support', 'service/work_mgmt',
 *   'field_ops', 'talent') — mirrors the keys each read-CF's own
 *   checkEntitlement gates on (grep: modules['<key>'] in functions/src).
 * @returns {boolean} true when the module is enabled OR entitlements are
 *   not (yet) definitively loaded (fail-open); false ONLY when a loaded
 *   modules map says modules[key] !== true.
 */
export function isModuleEnabled(key) {
  if (!key) return true;
  if (!_modules) return true; // unknown → fail-open (chrome only; CFs fail closed)
  return _modules[key] === true;
}

/** True once a definitive modules map has loaded (for callers that want to
 * distinguish "everything enabled" from "not loaded yet"). */
export function entitlementsLoaded() {
  return _modules !== null;
}

/** Test/sign-out hook — mirrors resetBootstrapCache(). */
export function resetEntitlementsCache() {
  _modules = null;
  _loadPromise = null;
}
